import { matchPath } from 'react-router-dom'
import { DEFAULT_SITE_SECTION, SITE_SECTION_LABEL, siteSectionOf } from '@/lib/siteSettings'

export interface Crumb {
  label: string
  to?: string
}

export const NAV_GROUP = {
  dashboard: 'Dashboard',
  portfolio: 'Carteira',
  relationship: 'Relacionamento',
  leasing: 'Locação',
  tools: 'Ferramentas',
  website: 'Site',
  settings: 'Configurações',
  agency: 'Imobiliária',
  account: 'Minha conta',
  admin: 'Administração'
} as const

const SETTINGS = '/empresas/:companyId/configuracoes'

const settingsPath = (params: Record<string, string | undefined>, suffix: string): string => `/empresas/${params.companyId}/configuracoes/${suffix}`

type TrailBuilder = (params: Record<string, string | undefined>) => Crumb[]

const dashboard = (): Crumb[] => [{ label: NAV_GROUP.dashboard }]
const portfolio = (): Crumb[] => [{ label: NAV_GROUP.portfolio }]
const relationship = (): Crumb[] => [{ label: NAV_GROUP.relationship }]
const leasing = (): Crumb[] => [{ label: NAV_GROUP.leasing }]
const tools = (): Crumb[] => [{ label: NAV_GROUP.tools }]
const agency = (): Crumb[] => [{ label: NAV_GROUP.agency }]
const settings = (p: Record<string, string | undefined>): Crumb[] => [{ label: NAV_GROUP.settings, to: settingsPath(p, 'geral/informacoes') }]
const admin = (): Crumb[] => [{ label: NAV_GROUP.admin }]

const TRAILS: [string, TrailBuilder][] = [
  ['/missoes', () => [{ label: 'Próximos passos', to: '/missoes' }]],
  ['/dashboard/site', () => [...dashboard(), { label: 'Site', to: '/dashboard/site' }]],
  ['/dashboard/vendas', () => [...dashboard(), { label: 'Vendas', to: '/dashboard/vendas' }]],
  ['/dashboard/financeiro', () => [...dashboard(), { label: 'Financeiro', to: '/dashboard/financeiro' }]],
  ['/imoveis', () => [...portfolio(), { label: 'Imóveis', to: '/imoveis' }]],
  ['/imoveis/novo-imovel', () => [...portfolio(), { label: 'Imóveis', to: '/imoveis' }, { label: 'Novo imóvel' }]],
  ['/imoveis/chaves', () => [...portfolio(), { label: 'Chaves', to: '/imoveis/chaves' }]],
  ['/imoveis/proprietarios', () => [...portfolio(), { label: 'Proprietários', to: '/imoveis/proprietarios' }]],
  ['/imoveis/proprietarios/novo', () => [...portfolio(), { label: 'Proprietários', to: '/imoveis/proprietarios' }, { label: 'Novo proprietário' }]],
  ['/imoveis/captacoes', () => [...portfolio(), { label: 'Captações', to: '/imoveis/captacoes' }]],
  ['/clientes', () => [...relationship(), { label: 'Clientes', to: '/clientes' }]],
  ['/clientes/novo-cliente', () => [...relationship(), { label: 'Clientes', to: '/clientes' }, { label: 'Novo cliente' }]],
  ['/crm', () => [...relationship(), { label: 'CRM', to: '/crm' }]],
  ['/crm/configuracoes', () => [...relationship(), { label: 'CRM', to: '/crm' }, { label: 'Configurações' }]],
  ['/chat', () => [...relationship(), { label: 'Chat', to: '/chat' }]],
  ['/contratos', () => [...leasing(), { label: 'Contratos', to: '/contratos' }]],
  ['/contratos/novo', () => [...leasing(), { label: 'Contratos', to: '/contratos' }, { label: 'Novo contrato' }]],
  ['/financeiro', () => [...leasing(), { label: 'Financeiro', to: '/financeiro' }]],
  ['/financeiro/recebimentos', () => [...leasing(), { label: 'Recebimentos', to: '/financeiro/recebimentos' }]],
  ['/studio', () => [...tools(), { label: 'Studio', to: '/studio' }]],
  ['/studio/novo', () => [...tools(), { label: 'Studio', to: '/studio' }, { label: 'Novo vídeo' }]],
  ['/blog', () => [...tools(), { label: 'Blog', to: '/blog' }]],
  ['/blog/novo', () => [...tools(), { label: 'Blog', to: '/blog' }, { label: 'Nova publicação' }]],
  ['/documentos', () => [...tools(), { label: 'Modelos de documento', to: '/documentos/modelos' }]],
  ['/documentos/modelos/novo', () => [...tools(), { label: 'Modelos de documento', to: '/documentos/modelos' }, { label: 'Novo modelo' }]],
  ['/empresas', () => [...agency(), { label: 'Imobiliárias', to: '/empresas' }]],
  [`${SETTINGS}/geral/informacoes`, p => [...settings(p), { label: 'Informações', to: settingsPath(p, 'geral/informacoes') }]],
  [`${SETTINGS}/geral/marca-dagua`, p => [...settings(p), { label: "Marca d'água", to: settingsPath(p, 'geral/marca-dagua') }]],
  [`${SETTINGS}/equipe`, p => [...settings(p), { label: 'Equipe', to: settingsPath(p, 'equipe') }]],
  [`${SETTINGS}/atividades`, p => [...settings(p), { label: 'Atividades', to: settingsPath(p, 'atividades') }]],
  [`${SETTINGS}/integracoes`, p => [...settings(p), { label: 'Integrações', to: settingsPath(p, 'integracoes') }]],
  [`${SETTINGS}/integracoes/instagram`, p => [...settings(p), { label: 'Integrações', to: settingsPath(p, 'integracoes') }, { label: 'Instagram' }]],
  [`${SETTINGS}/integracoes/whatsapp`, p => [...settings(p), { label: 'Integrações', to: settingsPath(p, 'integracoes') }, { label: 'WhatsApp' }]],
  [`${SETTINGS}/integracoes/olx`, p => [...settings(p), { label: 'Integrações', to: settingsPath(p, 'integracoes') }, { label: 'OLX' }]],
  [`${SETTINGS}/atendente-ia`, p => [...settings(p), { label: 'Atendente IA', to: settingsPath(p, 'atendente-ia') }]],
  [`${SETTINGS}/assinaturas`, p => [...settings(p), { label: 'Assinatura', to: settingsPath(p, 'assinaturas') }]],
  [
    `${SETTINGS}/site/:section`,
    p => [{ label: NAV_GROUP.website, to: settingsPath(p, 'site') }, { label: SITE_SECTION_LABEL[siteSectionOf(p.section) ?? DEFAULT_SITE_SECTION] }]
  ],
  ['/administracao/imobiliarias', () => [...admin(), { label: 'Imobiliárias', to: '/administracao/imobiliarias' }]],
  ['/administracao/agendamentos', () => [...admin(), { label: 'Agendamentos', to: '/administracao/agendamentos' }]],
  ['/administracao/saude', () => [...admin(), { label: 'Saúde dos serviços', to: '/administracao/saude' }]],
  ['/administracao/vendas', () => [...admin(), { label: 'Vendas', to: '/administracao/vendas' }]],
  ['/administracao/ia', () => [...admin(), { label: 'IA', to: '/administracao/ia' }]],
  ['/usuario', () => [{ label: NAV_GROUP.account, to: '/usuario' }]],
  [
    '/usuario/convites',
    () => [
      { label: NAV_GROUP.account, to: '/usuario' },
      { label: 'Convites', to: '/usuario/convites' }
    ]
  ]
]

const BY_SPECIFICITY = [...TRAILS].sort(([a], [b]) => b.split('/').length - a.split('/').length || b.length - a.length)

export function breadcrumbTrail(pathname: string): Crumb[] {
  if (pathname === '/' || pathname === '/dashboard') return dashboard()
  for (const [pattern, build] of BY_SPECIFICITY) {
    const exact = matchPath({ path: pattern, end: true }, pathname)
    if (exact) return build(exact.params)
  }
  for (const [pattern, build] of BY_SPECIFICITY) {
    const prefix = matchPath({ path: pattern, end: false }, pathname)
    if (prefix) return build(prefix.params)
  }
  return []
}

let leafLabel: string | null = null
let trailOverride: Crumb[] | null = null

const listeners = new Set<() => void>()

const notify = (): void => {
  for (const listener of [...listeners]) listener()
}

export const getBreadcrumbLeaf = (): string | null => leafLabel

export function setBreadcrumbLeaf(label: string | null): void {
  if (label === leafLabel) return
  leafLabel = label
  notify()
}

export const getBreadcrumbTrailOverride = (): Crumb[] | null => trailOverride

export function setBreadcrumbTrailOverride(trail: Crumb[] | null): void {
  if (trail === trailOverride) return
  trailOverride = trail
  notify()
}

export function subscribeBreadcrumb(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
